import type { ComponentPropsWithoutRef, ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";

import { cn } from "~/lib/utils";
import { isSafeMarkdownLink } from "./markdownSupport";

export interface MarkdownContentProps {
  readonly children: string;
  readonly className?: string;
  readonly variant?: "body" | "compact";
  readonly allowImages?: boolean;
}

function MarkdownLink(props: ComponentPropsWithoutRef<"a">) {
  const href = props.href && isSafeMarkdownLink(props.href) ? props.href : undefined;
  return (
    <a
      {...props}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium text-primary underline decoration-primary/35 underline-offset-2 hover:decoration-primary"
    />
  );
}

function MarkdownCode({ className, children, ...props }: ComponentPropsWithoutRef<"code">) {
  const fenced = className?.startsWith("language-") ?? false;
  return fenced ? (
    <code {...props} className={cn("font-mono text-[0.9em]", className)}>
      {children}
    </code>
  ) : (
    <code
      {...props}
      className="rounded bg-muted/55 px-1 py-0.5 font-mono text-[0.9em] text-foreground"
    >
      {children}
    </code>
  );
}

function MarkdownPre({ children }: { readonly children?: ReactNode }) {
  return (
    <pre className="my-3 overflow-x-auto rounded-md border border-border/60 bg-muted/30 p-3 text-sm leading-6">
      {children}
    </pre>
  );
}

const MARKDOWN_COMPONENTS = {
  a: MarkdownLink,
  blockquote: ({ children }: { readonly children?: ReactNode }) => (
    <blockquote className="my-3 border-l-2 border-border pl-3 text-muted-foreground">
      {children}
    </blockquote>
  ),
  code: MarkdownCode,
  h1: ({ children }: { readonly children?: ReactNode }) => (
    <h1 className="mb-2 mt-5 text-xl font-semibold tracking-tight first:mt-0">{children}</h1>
  ),
  h2: ({ children }: { readonly children?: ReactNode }) => (
    <h2 className="mb-2 mt-5 text-lg font-semibold tracking-tight first:mt-0">{children}</h2>
  ),
  h3: ({ children }: { readonly children?: ReactNode }) => (
    <h3 className="mb-1.5 mt-4 text-base font-semibold first:mt-0">{children}</h3>
  ),
  hr: () => <hr className="my-4 border-border/60" />,
  li: ({ children }: { readonly children?: ReactNode }) => <li className="pl-0.5">{children}</li>,
  ol: ({ children }: { readonly children?: ReactNode }) => (
    <ol className="my-2 list-decimal space-y-1 pl-5">{children}</ol>
  ),
  p: ({ children }: { readonly children?: ReactNode }) => (
    <p className="my-2 leading-6 first:mt-0 last:mb-0">{children}</p>
  ),
  pre: MarkdownPre,
  table: ({ children }: { readonly children?: ReactNode }) => (
    <div className="my-3 overflow-x-auto rounded-md border border-border/60">
      <table className="w-full border-collapse text-left text-sm">{children}</table>
    </div>
  ),
  td: ({ children }: { readonly children?: ReactNode }) => (
    <td className="border-t border-border/50 px-3 py-2 align-top">{children}</td>
  ),
  th: ({ children }: { readonly children?: ReactNode }) => (
    <th className="bg-muted/30 px-3 py-2 font-medium text-foreground">{children}</th>
  ),
  ul: ({ children }: { readonly children?: ReactNode }) => (
    <ul className="my-2 list-disc space-y-1 pl-5">{children}</ul>
  ),
};

const MARKDOWN_COMPONENTS_WITHOUT_IMAGES = {
  ...MARKDOWN_COMPONENTS,
  img: ({ alt }: ComponentPropsWithoutRef<"img">) => (
    <span className="text-muted-foreground">{alt ? `[Image: ${alt}]` : "[Image]"}</span>
  ),
};

/** Safe shared renderer for CMMD-authored Markdown. Raw HTML is never enabled. */
export function MarkdownContent({
  children,
  className,
  variant = "body",
  allowImages = true,
}: MarkdownContentProps) {
  return (
    <div
      className={cn(
        "min-w-0 break-words text-foreground [&_input[type=checkbox]]:mr-2 [&_input[type=checkbox]]:accent-primary",
        variant === "compact" ? "text-sm" : "text-base",
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkBreaks]}
        components={allowImages ? MARKDOWN_COMPONENTS : MARKDOWN_COMPONENTS_WITHOUT_IMAGES}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
